import type { CategoryColour } from '../../shared/api.ts';

/** What the tree accepts as a registry entry: the full CategoryEntry, or just
 *  a path with its optional colour — the tree only reads those two. */
export type CategoryEntryLike = { path: string; colour?: CategoryColour | null };

/** A category path split at its one separator. */
export type CategoryParts = { parent: string; child: string | null };

/** Split a category path into { parent, child }. A single-segment path has a
 *  null child. Returns null for an empty/missing path (Uncategorised). */
export function splitCategory(path: string | null | undefined): CategoryParts | null {
  if (!path || typeof path !== 'string') return null;
  const i = path.indexOf('/');
  if (i === -1) return { parent: path, child: null };
  return { parent: path.slice(0, i), child: path.slice(i + 1) };
}

/** The last segment of a path — what a tree row shows (a child shows its own
 *  name, a parent shows itself). */
export function categoryLeaf(path: string | null | undefined): string {
  const parts = splitCategory(path);
  return parts ? (parts.child ?? parts.parent) : '';
}

/** One row of the sidebar tree. `colour` is the registry's optional tint (the
 *  design's folder dot); null for an untinted category, and for the Bank, whose
 *  registry carries no colours at all. The decks registry has carried them
 *  since Run 0 and the Library's since Run 11. */
export type CategoryNode = { path: string; label: string; count: number; colour?: CategoryColour | null };
export type CategoryTreeNode = CategoryNode & { children: CategoryNode[] };

export type CategoryTree = {
  tree: CategoryTreeNode[];
  allCategories: string[];
  catCount: (path: string) => number;
};

/**
 * Build the sidebar category tree shared by the decks dashboard and the
 * Library. A category is a 1–2 segment `Parent/Child` path; a parent row shows
 * itself + all its children, and a child deck/item also counts toward its
 * parent. The registry (`categoryOrder`, incl. deliberately-empty categories)
 * fixes display order; paths present only on entities fall back to alphabetical
 * after the arranged ones. Case-insensitive throughout, first-seen casing wins.
 *
 * @param paths one entry per entity: a single path (Library items) or a LIST of
 *   them (decks, which sit in several). Falsy/empty entries (uncategorised) are
 *   ignored — count them yourself.
 * @param categoryOrder the saved registry / display order. Either bare paths
 *   (the Bank, and any client cached from before its shelf was widened) or
 *   `{ path, colour }` entries, which additionally tint their rows.
 */
export function buildCategoryTree(
  paths: Iterable<string | string[] | null | undefined>,
  categoryOrder: readonly (string | CategoryEntryLike)[] = [],
): CategoryTree {
  // One normalisation up front so the ordering and the colour lookup below read
  // the same list, whichever form the caller passed.
  const registry: CategoryEntryLike[] = (categoryOrder || [])
    .map((e) => (typeof e === 'string' ? { path: e, colour: null } : e))
    .filter((e): e is CategoryEntryLike => Boolean(e?.path));
  const colourOf = new Map(registry.map((e) => [e.path.toLowerCase(), e.colour ?? null]));
  const catCounts = new Map<string, number>();
  const pathDisplay = new Map<string, string>();
  const remember = (path: string) => {
    if (!pathDisplay.has(path.toLowerCase())) pathDisplay.set(path.toLowerCase(), path);
  };
  const bump = (path: string) => catCounts.set(path.toLowerCase(), (catCounts.get(path.toLowerCase()) || 0) + 1);

  for (const entry of paths) {
    // Counts are per ENTITY, not per path: a deck filed under two children of
    // the same parent must count once on that parent, or a sidebar row claims
    // more decks than the grid it opens can show.
    const counted = new Set();
    for (const category of Array.isArray(entry) ? entry : [entry]) {
      if (!category) continue;
      remember(category);
      if (!counted.has(category.toLowerCase())) {
        counted.add(category.toLowerCase());
        bump(category);
      }
      const slash = category.indexOf('/');
      if (slash !== -1) {
        const parent = category.slice(0, slash);
        remember(parent);
        if (!counted.has(parent.toLowerCase())) {
          counted.add(parent.toLowerCase());
          bump(parent);
        }
      }
    }
  }
  // Registry paths (incl. empty categories the user pre-created) join the tree,
  // and every child's parent is guaranteed a node.
  for (const e of registry) remember(e.path);
  for (const path of [...pathDisplay.values()]) {
    const slash = path.indexOf('/');
    if (slash !== -1) remember(path.slice(0, slash));
  }
  const catCount = (path: string) => catCounts.get(path.toLowerCase()) || 0;

  // Registry order wins; anything not yet arranged sorts alphabetically after.
  const orderIndex = new Map(registry.map((e, i) => [e.path.toLowerCase(), i]));
  function byCatOrder(a: string, b: string) {
    // One lookup each: every stored index is a number, so "absent" and
    // "undefined" cannot mean different things — `?? Infinity` is the same
    // answer the has()/get() pair gave.
    const ia = orderIndex.get(a.toLowerCase()) ?? Infinity;
    const ib = orderIndex.get(b.toLowerCase()) ?? Infinity;
    return ia !== ib ? ia - ib : a.localeCompare(b);
  }
  const allPaths = [...pathDisplay.values()];
  const allCategories = [...allPaths].sort(byCatOrder);
  const tree = allPaths
    .filter((p) => !p.includes('/'))
    .sort(byCatOrder)
    .map((parent) => ({
      path: parent,
      label: parent,
      count: catCount(parent),
      colour: colourOf.get(parent.toLowerCase()) ?? null,
      children: allPaths
        .filter((p) => p.toLowerCase().startsWith(parent.toLowerCase() + '/'))
        .sort(byCatOrder)
        .map((path) => ({
          path,
          label: categoryLeaf(path),
          count: catCount(path),
          colour: colourOf.get(path.toLowerCase()) ?? null,
        })),
    }));

  return { tree, allCategories, catCount };
}

/**
 * Does an entity's category path fall under the selected sidebar node? A parent
 * shows itself + all its children; a bare path matches only itself. Shared by
 * both screens' grid filters.
 */
export function categoryMatches(entityCategory: string | null | undefined, activePath: string): boolean {
  if (!entityCategory) return false;
  const a = activePath.toLowerCase();
  const c = entityCategory.toLowerCase();
  return c === a || c.startsWith(a + '/');
}
